import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import cn from 'class-names';

import Button from '@uikit/Form/Button/Button';
import NumberInput from '@uikit/Form/NumberInput/NumberInput';
import Radio from '@uikit/old/Radio/Radio';

import { getPayoutBankAccountFeePreviewApi, makeWithdrawalApi } from '@api/billing-publisher.api';
import { BillingPublisherWithdrawalRequestState } from 'respona_api/generated/common_pb';
import {
  BillingPublisherPayoutSettingsType,
  PayoutFeeOptionType,
} from '@ts/billing-publisher.types';
import { addNotification } from '@redux/actions/notifications.actions';
import { fetchPublisherBillingDetails } from '@redux/thunks/billings-publisher.thunks';
import { isBillingAddressUpdateRequiredError, parseApiErrorMessage } from '@utils/errorUtils';

import {
  formatUsd,
  getWisePayoutRecipient,
  isWiseFundedPayoutMethod,
  mapPaymentMethod,
} from '../../utils';

import ConfirmSummary from './ConfirmSummary';
import FeePreview from './FeePreview';

import './WithdrawForm.scss';

type Props = {
  paymentSettings: BillingPublisherPayoutSettingsType[];
  availableFunds: number;
  onCancel: () => void;
  onSuccess: () => void;
  onBillingAddressError: () => void;
};

function WithdrawForm({
  paymentSettings,
  availableFunds,
  onCancel,
  onSuccess,
  onBillingAddressError,
}: Props): JSX.Element {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const primaryMethod = paymentSettings[0];

  const [selectedMethod, setSelectedMethod] = useState<
    BillingPublisherPayoutSettingsType | undefined
  >(primaryMethod);
  const [withdrawAmount, setWithdrawAmount] = useState<number | ''>('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [feePreviewOptions, setFeePreviewOptions] = useState<PayoutFeeOptionType[] | null>(null);
  const [isFetchingFeePreview, setIsFetchingFeePreview] = useState(false);
  const [feePreviewError, setFeePreviewError] = useState<string | null>(null);

  const feePreviewSeqRef = useRef(0);
  const amountInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    amountInputRef.current?.focus();
  }, []);

  const resetFeePreview = () => {
    feePreviewSeqRef.current += 1;
    setFeePreviewOptions(null);
    setFeePreviewError(null);
    setIsFetchingFeePreview(false);
  };

  const activeMethod = selectedMethod ?? primaryMethod;
  const isWiseMethod = isWiseFundedPayoutMethod(activeMethod);
  const { currency: wiseCurrency, recipientId: wiseRecipientId } =
    getWisePayoutRecipient(activeMethod);

  const parsedAmount = Number(withdrawAmount);
  const isAmountValid =
    withdrawAmount !== '' &&
    !Number.isNaN(parsedAmount) &&
    parsedAmount > 0 &&
    parsedAmount <= availableFunds;

  const shouldFetchFeePreview = isWiseMethod && isAmountValid && !!wiseCurrency;

  useEffect(() => {
    if (!shouldFetchFeePreview) {
      resetFeePreview();
      return undefined;
    }

    feePreviewSeqRef.current += 1;
    const seq = feePreviewSeqRef.current;
    setIsFetchingFeePreview(true);
    setFeePreviewError(null);

    const timer = setTimeout(() => {
      getPayoutBankAccountFeePreviewApi(wiseCurrency, parsedAmount, wiseRecipientId || undefined)
        .then((resp) => {
          if (feePreviewSeqRef.current !== seq) return;
          setFeePreviewOptions(resp.optionsList ?? []);
        })
        .catch((e) => {
          if (feePreviewSeqRef.current !== seq) return;
          setFeePreviewError(parseApiErrorMessage(e) || 'Unable to load the fee preview.');
          setFeePreviewOptions(null);
        })
        .finally(() => {
          if (feePreviewSeqRef.current === seq) setIsFetchingFeePreview(false);
        });
    }, 400);

    return () => clearTimeout(timer);
  }, [shouldFetchFeePreview, parsedAmount, wiseCurrency, wiseRecipientId]);

  const inlineAmountError = (() => {
    if (withdrawAmount === '' || isAmountValid) return '';
    if (parsedAmount <= 0) return 'Amount must be greater than 0';
    if (parsedAmount > availableFunds) return 'Amount exceeds available funds';
    return 'Amount must be a number';
  })();

  const selectedFeeOption = useMemo(
    () => feePreviewOptions?.find((opt) => !opt.disabled) ?? null,
    [feePreviewOptions]
  );

  const canContinue = isAmountValid && !!activeMethod && (!isWiseMethod || !isFetchingFeePreview);

  const handleSelectMethod = (pm: BillingPublisherPayoutSettingsType) => {
    if (pm.paymentMethod === activeMethod?.paymentMethod) return;
    setSelectedMethod(pm);
    resetFeePreview();
    setIsConfirming(false);
  };

  const handleUseMax = () => {
    if (availableFunds <= 0) return;
    setWithdrawAmount(availableFunds);
  };

  const handleContinue = () => {
    if (!isAmountValid) {
      dispatch(addNotification({ type: 'error', title: 'Please enter a valid amount' }));
      amountInputRef.current?.focus();
      return;
    }
    if (!activeMethod) {
      dispatch(addNotification({ type: 'error', title: 'Select a payment method first.' }));
      return;
    }
    setIsConfirming(true);
  };

  const handleWithdraw = async () => {
    if (!activeMethod) {
      dispatch(
        addNotification({ type: 'error', title: 'Verify a payment method before withdrawing.' })
      );
      return;
    }
    setIsWithdrawing(true);
    try {
      const response = await makeWithdrawalApi(parsedAmount, activeMethod.paymentMethod);
      const serverMessage = response.message;

      dispatch(fetchPublisherBillingDetails(true));
      queryClient.invalidateQueries(['withdrawals-list']);

      if (!response.isSuccess) {
        dispatch(
          addNotification({
            type: 'error',
            title: serverMessage || 'Failed to submit withdrawal. Please try again.',
          })
        );
        return;
      }

      if (
        response.state === BillingPublisherWithdrawalRequestState.WITHDRAWAL_REQUEST_IN_PROCESSING
      ) {
        dispatch(
          addNotification({
            type: 'warning',
            title:
              serverMessage || 'Withdrawal submitted. It may take 1-3 business days to process.',
          })
        );
      } else {
        dispatch(
          addNotification({
            type: 'success',
            title: serverMessage || 'Withdrawal processed successfully.',
          })
        );
      }

      onSuccess();
    } catch (e) {
      if (isBillingAddressUpdateRequiredError(e)) {
        onBillingAddressError();
        dispatch(addNotification({ type: 'error', title: parseApiErrorMessage(e) }));
      } else {
        dispatch(
          addNotification({
            type: 'error',
            title: parseApiErrorMessage(e) || 'Failed to submit withdrawal. Please try again.',
          })
        );
      }
    } finally {
      setIsWithdrawing(false);
    }
  };

  const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isWithdrawing && !isConfirming && canContinue) {
      e.preventDefault();
      handleContinue();
    }
  };

  return (
    <div className="withdraw-form" role="group" aria-label="Withdraw funds">
      {paymentSettings.length > 1 && (
        <div className="withdraw-form__row">
          <div className="withdraw-form__label">
            <span>Withdraw to</span>
          </div>
          <div className="withdraw-form__control">
            <div className="withdraw-form__methods" role="radiogroup">
              {paymentSettings.map((pm) => {
                const isSelected = activeMethod?.paymentMethod === pm.paymentMethod;
                return (
                  <Radio
                    key={pm.paymentMethod}
                    className={cn('withdraw-form__method', {
                      'withdraw-form__method--selected': isSelected,
                    })}
                    value={String(pm.paymentMethod)}
                    isChecked={isSelected}
                    onChange={() => handleSelectMethod(pm)}
                    isDisabled={isConfirming || isWithdrawing}
                    testId={`publisher-withdraw-method-${pm.paymentMethod}`}
                  >
                    <span>{mapPaymentMethod(pm)}</span>
                  </Radio>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="withdraw-form__row">
        <div className="withdraw-form__label">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="withdraw-amount">Withdraw amount</label>
          <div className="withdraw-form__hint">Available: {formatUsd(availableFunds)}</div>
        </div>
        <div className="withdraw-form__control">
          <div className="withdraw-form__input-row">
            <NumberInput
              id="withdraw-amount"
              value={withdrawAmount}
              maxNumber={availableFunds}
              minNumber={0}
              inputRef={amountInputRef}
              disabled={isConfirming || isWithdrawing}
              onChange={(value) => setWithdrawAmount(value)}
              onKeyDown={handleAmountKeyDown}
              placeholder="0.00"
              data-testid="publisher-withdraw-amount"
            />
            <button
              type="button"
              className="withdraw-form__use-max"
              onClick={handleUseMax}
              data-testid="publisher-withdraw-max"
              disabled={availableFunds <= 0 || isWithdrawing || isConfirming}
            >
              Use max
            </button>
          </div>
          {!!inlineAmountError && (
            <div
              className="withdraw-form__inline-error"
              role="alert"
              data-testid="publisher-withdraw-error"
            >
              {inlineAmountError}
            </div>
          )}
        </div>
      </div>

      {isWiseMethod && (
        <div className="withdraw-form__row">
          <div className="withdraw-form__label">
            <span>Estimated transfer</span>
          </div>
          <div className="withdraw-form__control">
            <FeePreview
              isAmountValid={isAmountValid}
              isFetching={isFetchingFeePreview}
              error={feePreviewError}
              options={feePreviewOptions}
            />
          </div>
        </div>
      )}

      {isConfirming && (
        <ConfirmSummary amount={parsedAmount} method={activeMethod} feeOption={selectedFeeOption} />
      )}

      <div className="withdraw-form__actions">
        {isConfirming ? (
          <>
            <Button
              type="ghost"
              onClick={() => setIsConfirming(false)}
              disabled={isWithdrawing}
              data-testid="publisher-withdraw-back"
            >
              Back
            </Button>
            <Button
              type="primary"
              onClick={handleWithdraw}
              disabled={isWithdrawing}
              data-testid="publisher-withdraw-confirm"
              isLoading={isWithdrawing}
              loadingText="Withdrawing…"
            >
              {`Confirm withdrawal of ${formatUsd(parsedAmount)}`}
            </Button>
          </>
        ) : (
          <>
            <Button type="ghost" onClick={onCancel} data-testid="publisher-withdraw-cancel">
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleContinue}
              disabled={!canContinue}
              data-testid="publisher-withdraw-continue"
            >
              {isAmountValid ? `Continue · ${formatUsd(parsedAmount)}` : 'Continue'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default WithdrawForm;
